/**
 * RealtimeContext - Manages realtime notifications across the app
 * Single Responsibility: Only manages realtime state and events
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useSSE, SSE_EVENTS } from '../hooks/useSSE';
import { toast } from 'sonner';
import { Bell, UserPlus, CheckCircle, XCircle, CreditCard } from 'lucide-react';

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState([]);
  
  // Toast notification with action
  const showNotificationToast = useCallback((data, icon, onClick) => {
    toast(data.title, {
      description: data.message,
      icon: icon,
      duration: 8000,
      action: data.action_url ? {
        label: 'Ver',
        onClick: () => {
          if (onClick) onClick();
          else if (data.action_url) navigate(data.action_url);
        },
      } : undefined,
    });
  }, [navigate]);
  
  // Handle join request (for organizers)
  const handleJoinRequest = useCallback((data) => {
    console.log('SSE: New join request', data);
    setUnreadCount(prev => prev + 1);
    setRecentNotifications(prev => [data, ...prev.slice(0, 9)]);
    
    showNotificationToast(
      data,
      <UserPlus className="w-5 h-5 text-orange-500" />,
      () => navigate(`/trip/${data.trip_id}/members?tab=pending`)
    );
  }, [navigate, showNotificationToast]);
  
  // Handle join approved (for users)
  const handleJoinApproved = useCallback((data) => {
    console.log('SSE: Join approved', data);
    setUnreadCount(prev => prev + 1);
    setRecentNotifications(prev => [data, ...prev.slice(0, 9)]);
    
    showNotificationToast(
      data,
      <CheckCircle className="w-5 h-5 text-green-500" />,
      () => navigate(data.action_url || `/trip/${data.trip_id}`)
    );
  }, [navigate, showNotificationToast]);
  
  // Handle join rejected (for users)
  const handleJoinRejected = useCallback((data) => {
    console.log('SSE: Join rejected', data);
    setUnreadCount(prev => prev + 1);
    setRecentNotifications(prev => [data, ...prev.slice(0, 9)]);
    
    toast(data.title, {
      description: data.message,
      icon: <XCircle className="w-5 h-5 text-red-500" />,
      duration: 8000,
    });
  }, []);
  
  // Handle payment events
  const handlePaymentCreated = useCallback((data) => {
    console.log('SSE: Payment created', data);
    setUnreadCount(prev => prev + 1);
    
    showNotificationToast(
      data,
      <CreditCard className="w-5 h-5 text-blue-500" />,
      () => navigate(data.action_url)
    );
  }, [navigate, showNotificationToast]);
  
  const handlePaymentReceived = useCallback((data) => {
    console.log('SSE: Payment received', data);
    setUnreadCount(prev => prev + 1);
    
    showNotificationToast(
      data,
      <CreditCard className="w-5 h-5 text-green-500" />,
      () => navigate(data.action_url)
    );
  }, [navigate, showNotificationToast]);
  
  // Handle generic notification
  const handleNewNotification = useCallback((data) => {
    console.log('SSE: New notification', data);
    setUnreadCount(prev => prev + 1);
    setRecentNotifications(prev => [data, ...prev.slice(0, 9)]);
    
    showNotificationToast(
      data,
      <Bell className="w-5 h-5 text-orange-500" />,
      null
    );
  }, [showNotificationToast]);
  
  // Handle system connected
  const handleConnected = useCallback((data) => {
    console.log('SSE: Connected', data);
  }, []);
  
  // Handle any event (for debugging)
  const handleAnyEvent = useCallback((eventType, data) => {
    console.log(`SSE Event [${eventType}]:`, data);
  }, []);
  
  // SSE event handlers
  const sseHandlers = {
    [SSE_EVENTS.JOIN_REQUEST_NEW]: handleJoinRequest,
    [SSE_EVENTS.JOIN_REQUEST_APPROVED]: handleJoinApproved,
    [SSE_EVENTS.JOIN_REQUEST_REJECTED]: handleJoinRejected,
    [SSE_EVENTS.PAYMENT_CREATED]: handlePaymentCreated,
    [SSE_EVENTS.PAYMENT_RECEIVED]: handlePaymentReceived,
    [SSE_EVENTS.NOTIFICATION_NEW]: handleNewNotification,
    [SSE_EVENTS.SYSTEM_CONNECTED]: handleConnected,
    onEvent: handleAnyEvent,
  };
  
  // Connect to SSE only when authenticated
  const { isConnected, connectionError, reconnect } = useSSE(
    isAuthenticated ? token : null,
    sseHandlers
  );
  
  // Reset state on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      setRecentNotifications([]);
    }
  }, [isAuthenticated]);
  
  // Mark notifications as read
  const markAsRead = useCallback((count = 0) => {
    setUnreadCount(prev => Math.max(0, prev - count));
  }, []);
  
  // Clear recent notifications
  const clearRecentNotifications = useCallback(() => {
    setRecentNotifications([]);
  }, []);
  
  const value = {
    // Connection state
    isConnected,
    connectionError,
    reconnect,
    
    // Notifications state
    unreadCount,
    setUnreadCount,
    recentNotifications,
    
    // Actions
    markAsRead,
    clearRecentNotifications,
  };
  
  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
}

export default RealtimeContext;
